import { useCallback, useMemo, useEffect } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import { formatApiDateFromDate } from '@/utils/date';
import {
  useGetDiversificationCategoriesQuery,
  useGetHistoricalDiversificationQuery,
} from '@/store/diversificationsApi';
import { useGraphColors } from '@/hooks/useGraphColors';
import {
  DisplayMode,
  AssetAllocationDataRow,
  DIVERSIFICATION_ID,
  buildAssetAllocationDataset,
  getSortedCategoryNames,
  buildCategoryColorMap,
  getRowTotal,
} from './assetAllocationChartUtils';
import { useEffectiveExclusions } from '@/hooks/useEffectiveExclusions';
import { useAuthIsLoaded } from '@/hooks/useAuthIsLoaded';

interface UseAssetAllocationChartDataParams {
  startDate: Date;
  endDate: Date;
  displayMode: DisplayMode;
  onDataLoaded?: () => void;
  excludedAccounts?: number[];
  excludedCategories?: number[];
  excludedAccountTypes?: number[];
  excludedInstitutions?: number[];
  investorId?: number;
  investorScoped?: boolean;
}

interface UseAssetAllocationChartDataResult {
  dataSet: AssetAllocationDataRow[];
  allCategoryNames: string[];
  categoryColorMap: Record<string, string>;
  startTotalValue: number;
  startTotalDate: Date;
  endTotalValue: number;
  endTotalDate: Date;
  graphColors: string[];
  isLoading: boolean;
  isError: boolean;
  /** Whether either request is in flight, including a retry. */
  isFetching: boolean;
  /** Re-requests whichever of the categories and history requests failed. */
  refetch: () => void;
}

export function useAssetAllocationChartData({
  startDate,
  endDate,
  displayMode,
  onDataLoaded,
  excludedAccounts,
  excludedCategories,
  excludedAccountTypes,
  excludedInstitutions,
  investorId,
  investorScoped,
}: UseAssetAllocationChartDataParams): UseAssetAllocationChartDataResult {
  const graphColors = useGraphColors();
  const effectiveExclusions = useEffectiveExclusions({
    excludedAccounts,
    excludedCategories,
    excludedAccountTypes,
    excludedInstitutions,
    institutionsHandledByServer: investorScoped === true,
  });
  const startDateString = formatApiDateFromDate(startDate);
  const endDateString = formatApiDateFromDate(endDate);
  const authLoaded = useAuthIsLoaded();
  const investorResolved =
    investorScoped !== true || typeof investorId === 'number';

  const {
    data: catData,
    isLoading: isCategoriesLoading,
    isFetching: isCategoriesFetching,
    isError: isCategoriesError,
    refetch: refetchCategories,
  } = useGetDiversificationCategoriesQuery(undefined, { skip: !authLoaded });

  const {
    data: histData,
    isLoading: isHistLoading,
    isFetching: isHistFetching,
    isError: isHistError,
    refetch: refetchHistorical,
  } = useGetHistoricalDiversificationQuery(
    authLoaded && investorResolved
      ? {
          startDate: startDateString,
          endDate: endDateString,
          diversificationId: DIVERSIFICATION_ID,
          ...(typeof investorId === 'number' ? { InvestorId: investorId } : {}),
          ExcludedAccountTypes: effectiveExclusions.excludedAccountTypes,
          ExcludedCategories: effectiveExclusions.excludedCategories,
          ...(investorScoped === true
            ? {
                ExcludedInstitutions: effectiveExclusions.excludedInstitutions,
              }
            : {}),
          ...(investorScoped === true
            ? {}
            : { ExcludedAccounts: effectiveExclusions.excludedAccounts }),
        }
      : skipToken,
  );

  useEffect(() => {
    if (!isCategoriesLoading && !isHistLoading && catData && histData) {
      onDataLoaded?.();
    }
  }, [isCategoriesLoading, isHistLoading, catData, histData, onDataLoaded]);

  const categories = useMemo(() => catData?.Records ?? [], [catData?.Records]);

  const histRecords = useMemo(() => {
    const records = histData?.Records ?? [];
    return [...records].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [histData?.Records]);

  const dataSet = useMemo(
    () =>
      buildAssetAllocationDataset(
        histRecords,
        categories,
        startDate,
        endDate,
        displayMode,
      ),
    [histRecords, categories, displayMode, startDate, endDate],
  );

  const allCategoryNames = useMemo(
    () => getSortedCategoryNames(dataSet),
    [dataSet],
  );

  const categoryColorMap = useMemo(
    () => buildCategoryColorMap(histRecords, categories),
    [histRecords, categories],
  );

  const startTotalValue = useMemo(
    () => (dataSet.length ? getRowTotal(dataSet[0]) : 0),
    [dataSet],
  );

  const startTotalDate = useMemo(
    () => (dataSet.length ? dataSet[0].date : startDate),
    [dataSet, startDate],
  );

  const endTotalValue = useMemo(
    () => (dataSet.length ? getRowTotal(dataSet[dataSet.length - 1]) : 0),
    [dataSet],
  );

  const endTotalDate = useMemo(
    () => (dataSet.length ? dataSet[dataSet.length - 1].date : endDate),
    [dataSet, endDate],
  );

  const refetch = useCallback(() => {
    if (isCategoriesError) void refetchCategories();
    if (isHistError) void refetchHistorical();
  }, [isCategoriesError, isHistError, refetchCategories, refetchHistorical]);

  return {
    dataSet,
    allCategoryNames,
    categoryColorMap,
    startTotalValue,
    startTotalDate,
    endTotalValue,
    endTotalDate,
    graphColors,
    isLoading:
      !authLoaded || isCategoriesLoading || isHistLoading || isHistFetching,
    isError: isCategoriesError || isHistError,
    isFetching: isCategoriesFetching || isHistFetching,
    refetch,
  };
}
